import React, { useEffect, useState } from 'react';
import { Box } from '@wds/box';
import { Button } from '@wds/button';
import { Grid } from '@wds/grid';
import { IconInfo, IconKey, IconMobile } from '@wds/icons';
import { Popout } from '@wds/popout';
import { Switch } from '@wds/switch';
import { Typography } from '@wds/typography';
import { Tooltip } from '@wds/tooltip';

import PropTypes from 'prop-types';

import STRINGS from '../../../constants';
import CardTemplate from '../../../../sma.common/components/react/components/CardTemplate';
import ImageUpload from '../../../../sma.common/components/react/components/imageUpload/imageUpload';
import EditableTextField from '../../../../sma.common/components/react/components/EditableTextField';
import EditableSelectField from '../../../../sma.common/components/react/components/EditableSelectField';
import DeactivateMFAModal from '../../modals/DeactivateMFAModal';
import GenerateCodesModal from '../../modals/GenerateCodesModal';
import MFAEnrollmentModal from '../../modals/MFAEnrollmentModal';
import ChangeMFADeviceModal from '../../modals/ChangeMFADeviceModal';

const tabStrings = STRINGS.accountSettingsPage.myProfileTab;

const methodMap = {
	0: 'totp',
	1: 'email',
};

const MyProfileTab = (props) => {
	const {
		settings,
		setSettings,
		languages,
		timezones,
		cancelEmailChangeRequest,
		resendEmailChangeRequest,
		handleDisconnectGoogleSignOn,
		reset,
		editingFields,
		setEditingFields,
		assumedRole,
		usesSso,
		usesGoogleSSO,
		setToastInfo,
		isSuperuser,
	} = props;

	const [mfaEnabledCheckbox, setMfaEnabledCheckbox] = useState(settings?.mfa_enabled || false);
	const [showDeactivateMfaModal, setShowDeactivateMfaModal] = useState(false);
	const [showMFAEnrollmentModal, setShowMFAEnrollmentModal] = useState(false);
	const [enrolled, setEnrolled] = useState(settings?.mfa_enrolled || false);
	const [showGenerateCodesModal, setShowGenerateCodesModal] = useState(false);
	const [showChangeMFADeviceModal, setShowChangeMFADeviceModal] = useState(false);

	useEffect(() => {
		setMfaEnabledCheckbox(settings?.mfa_enabled);
	}, [settings]);

	useEffect(() => {
		setMfaEnabledCheckbox(settings?.mfa_enabled);
	}, [showDeactivateMfaModal, showDeactivateMfaModal]);

	const handleMfaEnabledChange = (e) => {
		setMfaEnabledCheckbox(e.target.checked);
		if (e.target.checked) {
			setShowMFAEnrollmentModal(true);
		} else {
			setShowDeactivateMfaModal(true);
		}
	};

	const handleCloseMFAModal = (completedEnrollment, method) => {
		if (completedEnrollment) {
			setEnrolled(true);
			setSettings((prevSettings) => ({
				...prevSettings,
				mfa_enabled: true,
				mfa_methods: [{ type: method === 'totp' ? 0 : 1 }],
			}));
		} else {
			setMfaEnabledCheckbox(false);
		}
		setShowMFAEnrollmentModal(false);
		setShowChangeMFADeviceModal(false);
	};

	const handleCloseChangeMFADeviceModal = (newMethod) => {
		if (newMethod) {
			setSettings((prevSettings) => ({
				...prevSettings,
				mfa_enabled: true,
				mfa_methods: [{ type: newMethod === 'totp' ? 0 : 1 }],
			}));
		}
		setShowMFAEnrollmentModal(false);
		setShowChangeMFADeviceModal(false);
	};

	const handleDeactivateMFA = () => {
		setShowDeactivateMfaModal(false);
		setEnrolled(false);
		setSettings((prevSettings) => ({
			...prevSettings,
			mfa_enabled: false,
		}));
		setToastInfo({
			show: true,
			color: 'success',
			title: STRINGS.toast.success,
			text: STRINGS.toast.mfaRemoved,
		});
	};

	return (
		<>
			<CardTemplate
				cardTitle={tabStrings.profileImage}
				id='profile-image'
			>
				<Box id='profile-image'>
					<Box px={3}>
						<ImageUpload
							label='Profile Image'
							id='profile-image'
							acceptTypes='image/png;base64'
							url='/internal/user/image/'
						/>
					</Box>
				</Box>
			</CardTemplate>
			<CardTemplate cardTitle={tabStrings.personalInfo} id='personal-info'>
				<Grid noBleeds>
					<Grid.Item>
						<Grid noBleeds>
							<Grid.Item>
								{Object.keys(settings).length > 0 && (
									<EditableTextField
										id='name'
										label={STRINGS.name}
										fields={[
											{
												id: 'first_name',
												label: STRINGS.firstName,
												type: 'text',
												isRequired: true,
												errorMessage: STRINGS.fieldErrors.text,
												value: settings?.first_name,
											},
											{
												id: 'last_name',
												label: STRINGS.lastName,
												type: 'text',
												isRequired: true,
												errorMessage: STRINGS.fieldErrors.text,
												value: settings?.last_name,
											},
										]}
										editable={(!usesSso && !usesGoogleSSO)}
										state={settings}
										setState={setSettings}
										reset={reset}
										editingFields={editingFields}
										setEditingFields={setEditingFields}
									/>
								)}
							</Grid.Item>
							<Grid.Item>
								{Object.keys(settings).length > 0 && (
									<>
										<EditableTextField
											id='email'
											label={STRINGS.email}
											fields={[
												{
													id: 'email',
													label: STRINGS.email,
													type: 'email',
													isRequired: true,
													errorMessage: STRINGS.fieldErrors.email,
													value: settings?.email,
												},
											]}
											editable={(!usesSso && !usesGoogleSSO && !settings?.temp_email)}
											state={settings}
											setState={setSettings}
											reset={reset}
											editingFields={editingFields}
											setEditingFields={setEditingFields}
										/>
										{settings.temp_email && (
											<Box mt={1} style={{ maxWidth: 350 }}>
												<Popout
													size='lg'
													trigger={(
														<Box display='flex' gap={2}>
															<IconInfo
																size='sm'
																title='info'
																color='darkMuted'
															/>
															<Typography
																variant='caption'
																weight='medium'
																lineHeight='1'
															>
																{STRINGS.emailAddressChangePending}
															</Typography>
														</Box>
													)}
												>
													<Popout.Body>
														{tabStrings.emailChangeRequested}
														{' '}
														{settings.temp_email}
														{'. '}
														{tabStrings.emailChangeCheckEmail}
													</Popout.Body>
													<Popout.Footer align='left'>
														<Button
															size='sm'
															color='warning'
															onClick={() => cancelEmailChangeRequest()}
														>
															{STRINGS.cancel}
														</Button>
														<Button
															size='sm'
															color='alt'
															onClick={() => resendEmailChangeRequest()}
														>
															{STRINGS.resendRequest}
														</Button>
													</Popout.Footer>
												</Popout>
											</Box>
										)}
									</>
								)}
							</Grid.Item>
						</Grid>
					</Grid.Item>
				</Grid>
				{!assumedRole && (
					<Grid noBleeds>
						{!usesGoogleSSO && (
							<Grid.Item xs={12}>
								<Grid mt={4} noBleeds>
									<Grid.Item>
										{Object.keys(settings).length > 0 && (
											<EditableTextField
												id='password'
												label={STRINGS.password}
												fields={[
													{
														id: 'current_password',
														label: STRINGS.currentPassword,
														type: 'password',
														errorMessage: STRINGS.fieldErrors.currentPassword,
														value: settings?.current_password || '',
														isRequired: true,
													},
													{
														id: 'new_password',
														label: STRINGS.newPassword,
														type: 'password',
														errorMessage: STRINGS.fieldErrors.newPassword,
														value: settings?.new_password || '',
													},
												]}
												state={settings}
												setState={setSettings}
												reset={reset}
												editingFields={editingFields}
												setEditingFields={setEditingFields}
											/>
										)}
									</Grid.Item>
								</Grid>
							</Grid.Item>
						)}
						{!usesSso && !usesGoogleSSO && !assumedRole && !isSuperuser && (
							<>
								<Grid.Item xs={12}>
									<Grid noBleeds>
										<Grid.Item>
											<Box display='flex' justifyContent='space-between'>
												<Box display='flex' gap={2}>
													<Typography
														weight='medium'
														id='mfa-switch-label'
													>
														{tabStrings.mfaEnabled}
													</Typography>
												</Box>
												{!settings.can_disable_mfa ? (
													<Tooltip text={STRINGS.site_requires_mfa}>
														<Switch
															checked={mfaEnabledCheckbox}
															id='mfa-enabled'
															aria-labelledby='mfa-switch-label'
															aria-label={STRINGS.labels.mfaSwitch}
															disabled={!settings.can_disable_mfa}
															aria-disabled={!settings.can_disable_mfa}
														/>
													</Tooltip>
												) : (
													<Switch
														checked={mfaEnabledCheckbox}
														onChange={(e) => handleMfaEnabledChange(e)}
														id='mfa-enabled'
														aria-labelledby='mfa-switch-label'
														aria-label={STRINGS.labels.mfaSwitch}
														disabled={!settings.can_disable_mfa}
														aria-disabled={!settings.can_disable_mfa}
													/>
												)}
											</Box>
										</Grid.Item>
									</Grid>
								</Grid.Item>
								<Grid.Item>
									{settings.mfa_enabled ? (
										settings?.mfa_methods && Object.entries(settings?.mfa_methods[0]).length > 0 && (
											<Box display='flex' gap={2}>
												<Typography weight='medium'>{tabStrings.enrolledMethod}:</Typography>
												<Typography>
													{tabStrings?.[methodMap[settings?.mfa_methods[0].type]]}
												</Typography>
											</Box>
										)
									) : (
										<Box display='flex' gap={2}>
											<Typography>
												{tabStrings.mfaLearnMoreText}
											</Typography>
											<Typography
												component='a'
												href={reverse('mfa-learn-more')}
												target='_blank'
												rel='noopener noreferrer'
												variant='link'
											>
												{tabStrings.mfaLearnMoreLink}
											</Typography>
										</Box>
									)}
								</Grid.Item>
							</>
						)}
					</Grid>
				)}
				{
					settings?.mfa_enabled && !assumedRole && (
						<Grid noBleeds mt={4}>
							<Grid.Item>
								<Box
									display='flex'
									gap={4}
									justifyContent='flex-start'
									flexWrap
								>
									<Button
										color='alt'
										id='change-device'
										onClick={() => setShowChangeMFADeviceModal(true)}
									>

										<IconMobile size='sm' title='mobile_icon' />
										{tabStrings.changeEnrolledDevice}
									</Button>
									<Button
										color='alt'
										id='generate-codes'
										onClick={() => setShowGenerateCodesModal(true)}
									>
										<IconKey size='sm' title='key_icon' />
										{tabStrings.generateNewCodes}
									</Button>
								</Box>
							</Grid.Item>
						</Grid>
					)
				}
			</CardTemplate>
			{(!assumedRole || assumedRole.super) && settings?.social && Object.keys(settings?.social).length > 0 && (
				<CardTemplate
					cardTitle={tabStrings.linkedAccounts}
					id='linked-accounts'
				>
					<Box id='linked-accounts-id'>
						<Grid noBleeds xs={16}>
							<Grid.Item xs={12}>
								<Box>
									<Box>
										<Typography
											variant='bodySm'
											weight='medium'
										>
											{tabStrings.socialSignOn}
										</Typography>
									</Box>
									<Box display='flex' justifyContent='space-between'>
										<Typography
											lineHeight={2.5}
											color='link'
										>
											{STRINGS.googleSSO}
											{' '}
											(
											{settings.social.id}
											)
										</Typography>
										<Button
											color='warning'
											onClick={() => handleDisconnectGoogleSignOn()}
											id='disable-google'
										>
											{tabStrings.disconnectAccount}
										</Button>
									</Box>
								</Box>
							</Grid.Item>

						</Grid>
					</Box>
				</CardTemplate>
			)}
			<CardTemplate
				cardTitle={tabStrings.generalPreferences}
				id='general-preferences'
			>
				<Grid noBleeds>
					<Grid.Item>
						<Grid noBleeds>
							<Grid.Item>
								<Box>
									{Object.keys(languages).length > 0 && Object.keys(settings).length > 0 && (
										<EditableSelectField
											id='language'
											label={STRINGS.preferredLanguage}
											fields={[
												{
													id: 'language_preference',
													label: STRINGS.preferredLanguage,
													type: 'select',
													isRequired: true,
													errorMessage: STRINGS.fieldErrors.language,
													defaultValue: settings?.language_preference,
													options: languages.all_languages,
												},
											]}
											state={settings}
											setState={setSettings}
											reset={reset}
											editingFields={editingFields}
											setEditingFields={setEditingFields}
										/>
									)}
								</Box>
							</Grid.Item>
							<Grid.Item>
								<Box>
									{Object.keys(timezones).length > 0 && Object.keys(settings).length > 0 && (
										<EditableSelectField
											id='timezone'
											label={STRINGS.timezone}
											fields={[
												{
													id: 'timezone',
													label: STRINGS.timezone,
													type: 'select',
													isRequired: true,
													errorMessage: STRINGS.fieldErrors.timezone,
													defaultValue: settings?.timezone,
													options: timezones,
												},
											]}
											state={settings}
											setState={setSettings}
											reset={reset}
											editingFields={editingFields}
											setEditingFields={setEditingFields}
										/>
									)}
								</Box>
							</Grid.Item>
						</Grid>
					</Grid.Item>
				</Grid>
			</CardTemplate>
			{showMFAEnrollmentModal && (
				<MFAEnrollmentModal
					open={showMFAEnrollmentModal}
					enrolled={enrolled}
					setEnrolled={setEnrolled}
					handleClose={handleCloseMFAModal}
				/>
			)}
			{showGenerateCodesModal && (
				<GenerateCodesModal
					open={showGenerateCodesModal}
					setOpen={setShowGenerateCodesModal}
				/>
			)}
			{showDeactivateMfaModal && (
				<DeactivateMFAModal
					open={showDeactivateMfaModal}
					setOpen={setShowDeactivateMfaModal}
					handleOnSuccess={handleDeactivateMFA}
					method={methodMap[settings?.mfa_methods[0].type]}
				/>
			)}
			{showChangeMFADeviceModal && (
				<ChangeMFADeviceModal
					open={showChangeMFADeviceModal}
					setOpen={setShowChangeMFADeviceModal}
					handleClose={handleCloseChangeMFADeviceModal}
				/>
			)}
		</>
	);
};

MyProfileTab.propTypes = {
	settings: PropTypes.object.isRequired,
	languages: PropTypes.object.isRequired,
	timezones: PropTypes.array.isRequired,
	usesSso: PropTypes.bool.isRequired,
	usesGoogleSSO: PropTypes.bool.isRequired,
	setSettings: PropTypes.func.isRequired,
	reset: PropTypes.bool.isRequired,
	editingFields: PropTypes.object.isRequired,
	setEditingFields: PropTypes.func.isRequired,
	cancelEmailChangeRequest: PropTypes.func.isRequired,
	resendEmailChangeRequest: PropTypes.func.isRequired,
	handleDisconnectGoogleSignOn: PropTypes.func.isRequired,
	assumedRole: PropTypes.object,
	isSuperuser: PropTypes.bool,
};

MyProfileTab.defaultProps = { assumedRole: {}, isSuperuser: false };

export default MyProfileTab;
